import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { DiagnosisRow, DIAGNOSIS_STATUS } from './useDiagnosisData';
import { filterDiagnosisRows } from './searchUtils';
import {
  Box,
  TextField,
  Typography,
  Stack,
  Chip,
  Radio,
  Paper,
  Divider,
  IconButton,
  InputAdornment,
  Alert,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { styled } from '@mui/material/styles';
import DoneIcon from '@mui/icons-material/Done';
import InfoIcon from '@mui/icons-material/Info';

interface DiagnosisCodeSelectionProps {
  title?: string;
  type: 'primary' | 'metastasis' | null;
  selectedDiagnosis: DiagnosisRow | null;
  selectedMetastasisDiagnosis?: DiagnosisRow | null;
  onSelectionChange?: (selectedDiagnosis: DiagnosisRow | null) => void;
  data: DiagnosisRow[];
  errorString?: string;
  errorRef?: React.RefObject<HTMLDivElement>;
  currentDiagnosisType: 'primary' | 'metastasis' | null;
  isSubmitted: boolean;
  hasTreatmentStarted?: boolean;
}

const SearchTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
  },
}));

const getRowId = (row: DiagnosisRow) => {
  if (!row) {
    return null;
  }

  // Use a more robust key that handles undefined values
  const id = row.id || 'no-id';
  const diagnosisCodeId = row.diagnosisCodeId || 'no-diagnosis-code-id';
  const code = row.code || 'no-code';

  return `${id}-${diagnosisCodeId}-${code}`;
};

const EmptyFilterResults = () => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="body1" sx={{ paddingBottom: '16px' }}>
        No results found
      </Typography>
      <Typography variant="body2" paddingBottom={1}>
        TRY
      </Typography>
      <ul style={{ listStyleType: 'disc', paddingLeft: '16px', marginLeft: '8px' }}>
        <li style={{ paddingBottom: '8px' }}> Adjusting or clearing some filters</li>
        <li>Checking if there are any active search terms</li>
      </ul>
    </Box>
  );
};

const EmptySearchResults = ({ searchQuery }: { searchQuery: string }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="body1" sx={{ paddingBottom: '16px' }}>
        No results found for "{searchQuery}"
      </Typography>
      <Typography variant="body2" paddingBottom={1}>
        SEARCH TIPS
      </Typography>
      <ul style={{ listStyleType: 'disc', paddingLeft: '16px', marginLeft: '8px' }}>
        <li style={{ paddingBottom: '8px' }}>Check if you have any active filters applied.</li>
        <li>Verify your spelling and try again.</li>
      </ul>
    </Box>
  );
};

const MAX_RESULTS = 50;

const COLUMN_WIDTHS = ['30%', '40%', '30%'];

const DiagnosisCodeSelection: React.FC<DiagnosisCodeSelectionProps> = ({
  title,
  type,
  onSelectionChange,
  data,
  selectedDiagnosis,
  selectedMetastasisDiagnosis,
  errorString,
  errorRef,
  currentDiagnosisType,
  isSubmitted,
  hasTreatmentStarted,
}) => {
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const [filters, setFilters] = useState<Set<'AI Recommended' | 'Existing'>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Memoize the filtered data
  const filteredData = useMemo(
    () => filterDiagnosisRows(data, searchQuery, filters.size > 0 ? Array.from(filters) : null).slice(0, MAX_RESULTS),
    [data, searchQuery, filters],
  );

  useEffect(() => {
    if (selectedDiagnosis) {
      setSelectedRow(getRowId(selectedDiagnosis));
    } else {
      setSelectedRow(null);
    }
  }, [selectedDiagnosis, currentDiagnosisType]);

  useEffect(() => {
    if (currentDiagnosisType) {
      setFilters(new Set([]));
      setSearchQuery('');
    }
  }, [currentDiagnosisType]);

  const getStatusIcon = (status: string) => {
    if (['AI Recommended', 'AI Recommended - high certainty'].includes(status)) {
      return <AutoAwesomeIcon sx={{ color: 'primary.main' }} />;
    }
    return <></>;
  };

  const handleRowSelection = useCallback(
    (row: DiagnosisRow) => {
      const rowId = getRowId(row);
      if (rowId !== selectedRow) {
        onSelectionChange?.(row);
      }
    },
    [selectedRow, onSelectionChange],
  );

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const toggleFilter = (filter: 'AI Recommended' | 'Existing') => {
    const newFilters = new Set(filters);
    if (newFilters.has(filter)) {
      newFilters.delete(filter);
    } else {
      newFilters.add(filter);
    }
    setFilters(newFilters);
  };

  const getTableDisabledMessage = (): string => {
    if (hasTreatmentStarted) {
      return 'The diagnosis cannot be changed once treatment has started';
    }

    if (type === 'metastasis' && isSubmitted) {
      return 'Once a metastasis diagnosis has been submitted, it cannot be modified';
    }

    if (type === 'primary' && isSubmitted && currentDiagnosisType === 'metastasis') {
      return 'Once a metastasis diagnosis has been submitted, it cannot be modified';
    }

    if (
      type === 'primary' &&
      isSubmitted &&
      hasTreatmentStarted &&
      selectedDiagnosis?.status === DIAGNOSIS_STATUS.EXISTING
    ) {
      return 'Once an existing primary diagnosis has been submitted, it cannot be changed';
    }

    if (type === 'primary' && selectedMetastasisDiagnosis?.status === DIAGNOSIS_STATUS.EXISTING) {
      return 'Cannot change the primary diagnosis of an existing metastasis diagnosis';
    }

    return '';
  };
  const tableDisabledMessage = getTableDisabledMessage();

  const isRowDisabled = (row: DiagnosisRow): string => {
    // Returns a message to display in the tooltip if the given row is disabled.
    // Returns an empty string if it's fine, which nullifies the toolip, and let's us use Boolean(error) for the disabled state
    const isCurrentlySelected = selectedRow === getRowId(row);
    if (tableDisabledMessage && !isCurrentlySelected) return tableDisabledMessage;

    if (!tableDisabledMessage && isSubmitted && row.status === DIAGNOSIS_STATUS.EXISTING) {
      return 'Once a new primary diagnosis has been submitted, it cannot be changed to an existing diagnosis.';
    }

    return '';
  };

  const getTextColour = (row: DiagnosisRow) => {
    if (selectedRow === getRowId(row)) {
      return 'text.primary';
    }

    const isDisabled = Boolean(isRowDisabled(row));
    if (isDisabled) {
      return 'text.disabled';
    }
    return 'text.primary';
  };

  // Row style helper
  const getRowSx = (isSelected: boolean, isExisting: boolean, isDisabled: boolean, theme: any) => {
    let backgroundColor = 'inherit';
    if (isSelected) {
      backgroundColor = theme.palette.action.selected;
    }

    let hoverBackgroundColor = backgroundColor;
    if (!isDisabled) {
      hoverBackgroundColor = theme.palette.action.selected;
    }

    return {
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      backgroundColor,
      '&:hover': {
        backgroundColor: hoverBackgroundColor,
      },
      opacity: isDisabled ? 0.7 : 1,
    };
  };
  const getSelectedPrimaryDiagnosisText = (): string => {
    /*
     * Selected: {code} {tumour}
     * Related Dx: {code} {tumour}
     * Related Dx: -
     */
    if (!selectedDiagnosis?.code) {
      return '';
    }

    const primaryCode = selectedDiagnosis ? `${selectedDiagnosis.code} ${selectedDiagnosis.tumourStream?.name}` : '-';
    if (selectedMetastasisDiagnosis) {
      return `Related Dx: ${primaryCode}`;
    }
    return `Selected Dx: ${primaryCode} `;
  };
  const getSelectedMetastasisDiagnosisText = (): string => {
    /*
     * Selected: Existing Metastasis Dx: {code} | Related Dx: Unknown
     * Selected: Existing Metastasis Dx: {code} | Related Dx: {code} {tumour}
     * Selected: New Metastasis Dx: {code} | Related Dx: -
     */
    const metsCode = selectedMetastasisDiagnosis ? selectedMetastasisDiagnosis.code : '-';
    if (!metsCode) {
      return '';
    }

    if (selectedMetastasisDiagnosis?.status === DIAGNOSIS_STATUS.EXISTING) {
      return `Selected: Existing Metastatic Dx: ${metsCode}`;
    }

    return `Selected: New Metastatic Dx: ${metsCode} `;
  };

  const showUpdateBanner = () => {
    if (type === 'primary' && isSubmitted && !hasTreatmentStarted && !selectedMetastasisDiagnosis) {
      return true;
    }
    return false;
  };

  return (
    <Paper sx={{ width: '100%', minWidth: '900px' }}>
      <Stack direction="column">
        <Typography variant="subtitle1" sx={{ padding: '16px', paddingBottom: '0px' }}>
          {title}
        </Typography>
        {(type === 'primary' && selectedMetastasisDiagnosis && (
          <Typography variant="body2" color="text.secondary" sx={{ marginLeft: '16px', marginBottom: '8px' }}>
            {`${getSelectedMetastasisDiagnosisText()} | ${getSelectedPrimaryDiagnosisText()}`}
          </Typography>
        )) || (
          <Typography variant="body2" color="text.secondary" sx={{ marginLeft: '16px', marginBottom: '8px' }}>
            {getSelectedPrimaryDiagnosisText()}
          </Typography>
        )}
        {showUpdateBanner() && (
          <Paper sx={{ padding: '16px', backgroundColor: '#F0F7FD', margin: '8px 16px 16px 16px' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <InfoIcon className="icon icon-form-infoicon" sx={{ color: 'info.dark' }} />
              <Typography variant="body2" className="variation-infoicon-text" sx={{ color: 'text.primary' }}>
                Selecting a new diagnosis will overwrite the existing diagnosis in the EMR.
              </Typography>
            </Stack>
          </Paper>
        )}
        <Divider />
        <Stack direction="column" spacing={2} sx={{ padding: '16px' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchTextField
              data-testid={`${type}-diagnosis-search`}
              disabled={Boolean(tableDisabledMessage)}
              inputRef={searchInputRef}
              placeholder="ICD code/description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ minWidth: '380px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {type === 'primary' && (
              <Stack direction="row" spacing={1} alignItems="center" id="diagnosis-filters">
                <Typography>Filters:</Typography>
                <Chip
                  data-testid="recommended-filter"
                  label="AI Recommended"
                  icon={filters.has('AI Recommended') ? <DoneIcon /> : undefined}
                  onClick={() => toggleFilter('AI Recommended')}
                  color="default"
                  size="medium"
                  disabled={Boolean(tableDisabledMessage)}
                  variant={filters.has('AI Recommended') ? 'filled' : 'outlined'}
                />
                <Chip
                  data-testid="existing-filter"
                  label="Existing"
                  icon={filters.has('Existing') ? <DoneIcon /> : undefined}
                  onClick={() => toggleFilter('Existing')}
                  color="default"
                  size="medium"
                  disabled={Boolean(tableDisabledMessage)}
                  variant={filters.has('Existing') ? 'filled' : 'outlined'}
                />
              </Stack>
            )}
          </Stack>
          {errorString && (
            <Alert severity="error" ref={errorRef} data-testid={`${type}-diagnosis-error`}>
              {errorString}
            </Alert>
          )}
          <Box sx={{ width: '100%', minHeight: '400px' }} data-testid={`${type}-diagnosis-table-container`}>
            {filteredData.length > 0 ? (
              <TableContainer sx={{ maxHeight: 386, overflowY: 'auto' }}>
                <Table data-testid={`${type}-diagnosis-table`} sx={{ tableLayout: 'fixed' }}>
                  <TableHead
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      backgroundColor: (theme) => theme.palette.background.paper,
                    }}>
                    <TableRow>
                      <TableCell
                        sx={{ width: COLUMN_WIDTHS[0], fontWeight: 500, padding: '8px 16px', height: '36px' }}>{`${
                        type === 'primary' ? 'Primary' : 'Metastatic'
                      } Diagnosis Code`}</TableCell>
                      <TableCell
                        sx={{
                          width: COLUMN_WIDTHS[1],
                          fontWeight: 500,
                          padding: '8px 16px',
                          height: '36px',
                        }}>
                        Description
                      </TableCell>
                      <TableCell sx={{ width: COLUMN_WIDTHS[2], fontWeight: 500, padding: '8px 16px', height: '36px' }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // Find the last index of 'Existing - Found in EMR' status
                      const lastExistingIdx = filteredData
                        .map((row) => row.status)
                        .lastIndexOf(DIAGNOSIS_STATUS.EXISTING);
                      const hasExisting = lastExistingIdx !== -1;
                      const hasNew = filteredData.some((row) => row.status !== DIAGNOSIS_STATUS.EXISTING);

                      return filteredData.map((row, idx) => {
                        const rowId = getRowId(row);
                        const uniqueKey = `${rowId}-${idx}`;
                        const isDisabled = Boolean(isRowDisabled(row));
                        const isSelected = rowId === selectedRow;
                        const isExisting = row.status === DIAGNOSIS_STATUS.EXISTING;
                        const shouldShowDivider = hasExisting && hasNew && idx === lastExistingIdx;
                        return (
                          <React.Fragment key={uniqueKey}>
                            <Tooltip
                              followCursor={true}
                              title={isRowDisabled(row)}
                              enterDelay={250}
                              enterNextDelay={250}
                              leaveDelay={0}>
                              <TableRow
                                sx={(theme) => getRowSx(isSelected, isExisting, isDisabled, theme)}
                                onClick={() => {
                                  !isDisabled && handleRowSelection(row);
                                }}
                                data-testid={`${type}-diagnosis-table-row`}>
                                <TableCell sx={{ width: COLUMN_WIDTHS[0], padding: '8px 16px', height: '40px' }}>
                                  <Stack direction="row" alignItems="center" gap="8px">
                                    <Radio
                                      data-testid={`${type}-diagnosis-radio-${row.code}`}
                                      disabled={isDisabled}
                                      checked={isSelected}
                                      size="small"
                                      sx={{ padding: '3px' }}
                                      inputProps={{
                                        'aria-label': row.code,
                                        'aria-checked': isSelected,
                                      }}
                                    />
                                    <Stack direction="column" justifyContent="center">
                                      <Typography variant="body2" noWrap color={getTextColour(row)}>
                                        {row.code} {row.tumourStream?.name}
                                      </Typography>
                                      {row.dxDate && (
                                        <Typography variant="body2" color="text.secondary">
                                          Dx date: {row.dxDate}
                                        </Typography>
                                      )}
                                    </Stack>
                                  </Stack>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    width: COLUMN_WIDTHS[1],
                                    padding: '8px 16px',
                                    height: '40px',
                                  }}>
                                  <Stack direction="column" justifyContent="center">
                                    <Typography variant="body2" noWrap color={getTextColour(row)}>
                                      {row.description}
                                    </Typography>
                                    {type === 'metastasis' && row?.status === DIAGNOSIS_STATUS.EXISTING && (
                                      <Typography variant="body2" noWrap color={'text.secondary'}>
                                        Related primary:{' '}
                                        {row.relatedPrimaryDiagnosis
                                          ? `${row.relatedPrimaryDiagnosis.code}: ${row.relatedPrimaryDiagnosis.tumourStream?.name}: ${row.relatedPrimaryDiagnosis.description}`
                                          : 'Unknown'}
                                      </Typography>
                                    )}
                                    {row.details && (
                                      <Typography variant="body2" color="text.secondary">
                                        {row.details}
                                      </Typography>
                                    )}
                                  </Stack>
                                </TableCell>
                                <TableCell sx={{ width: COLUMN_WIDTHS[2], padding: '8px 16px', height: '40px' }}>
                                  <Stack direction="row" alignItems="flex-start" justifyContent="flex-start" gap="8px">
                                    {getStatusIcon(row.status)}
                                    {row.status === 'AI Recommended - high certainty' ? (
                                      <Stack direction="column">
                                        <Typography variant="body2" noWrap color={getTextColour(row)}>
                                          AI Recommended
                                        </Typography>
                                        <Typography variant="caption" noWrap color={'text.secondary'}>
                                          High certainty
                                        </Typography>
                                      </Stack>
                                    ) : (
                                      <Typography variant="body2" noWrap color={getTextColour(row)}>
                                        {row.status}
                                      </Typography>
                                    )}
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            </Tooltip>
                            {shouldShowDivider && (
                              <TableRow key={`${uniqueKey}-divider`}>
                                <TableCell
                                  colSpan={3}
                                  sx={{ textAlign: 'center', padding: '0px', borderBottom: 'none' }}>
                                  <Box display="flex" alignItems="center" width="100%">
                                    <Divider sx={{ borderColor: 'grey.300', display: 'flex', flex: 1 }} />
                                    <Typography variant="overline" color="primary" sx={{ mx: 2 }}>
                                      NEW DIAGNOSES
                                    </Typography>
                                    <Divider sx={{ borderColor: 'grey.300', display: 'flex', flex: 1 }} />
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : searchQuery ? (
              <EmptySearchResults searchQuery={searchQuery} />
            ) : (
              <EmptyFilterResults />
            )}
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
};

export type { DiagnosisRow };
export default DiagnosisCodeSelection;
